import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

/* Rider shift absence (order-service `/order/rider-shift`): a Rider Manager marks
   every shift of a rider's day absent so the payout run pays ₹0 for it. */

export type RiderShiftAbsenceReasonCode =
  | 'NOT_AT_ASSIGNED_LOCATION'
  | 'SIGNED_IN_WITH_SHARED_QR'
  | 'LEFT_WITHOUT_WORKING'
  | 'UNREACHABLE'
  | 'OTHER';

export type RiderShiftAbsence = {
  active: boolean;
  reasonCode: RiderShiftAbsenceReasonCode | string | null;
  note: string | null;
  markedBy: string | null;
  markedAt: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
  revokeNote: string | null;
};

export type RiderShiftAbsenceEntry = {
  shiftId: number;
  riderId: number;
  facilityId: number | null;
  facilityName: string | null;
  status: 'ACTIVE' | 'ENDED' | 'BLOCKED' | string;
  startedAt: string | null;
  endedAt: string | null;
  businessDate: string;
  version: number;
  canEdit: boolean;
  absence: RiderShiftAbsence | null;
};

export type RiderShiftAbsenceDay = {
  riderId: number;
  businessDate: string;
  editable: boolean;
  editableUntil: string | null;
  shifts: Array<RiderShiftAbsenceEntry>;
};

export type RiderShiftAbsenceMarkRequest = {
  reasonCode: RiderShiftAbsenceReasonCode;
  note: string;
  expectedVersion: number;
};

export type RiderShiftAbsenceRevokeRequest = {
  note: string;
  expectedVersion: number;
};

type Envelope = {
  data?: unknown;
  success?: boolean;
  status?: { type?: string; message?: string; key?: string; code?: number };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

/* The response is accepted enveloped ({ success, status, data }) or plain: the
   payload is recognised by its shape (`shifts` for a day, `shiftId` for a
   shift), so either form works without guessing which one the server sends. */
export const unwrapRiderShiftAbsence = <T>(
  body: unknown,
  looksRight: (value: Record<string, unknown>) => boolean,
  fallback: string,
): T => {
  if (isObject(body)) {
    const envelope = body as Envelope;
    if (envelope.success === false || envelope.status?.type === 'ERROR') {
      throw Object.assign(new Error(envelope.status?.message || envelope.status?.key || fallback), {
        statusCode: envelope.status?.code,
        status: envelope.status,
      });
    }
    if (looksRight(body)) return body as T;
    if (isObject(envelope.data) && looksRight(envelope.data)) return envelope.data as T;
  }
  throw new Error(fallback);
};

const isDay = (value: Record<string, unknown>) => Array.isArray(value.shifts);
const isEntry = (value: Record<string, unknown>) => value.shiftId != null;

class RiderShiftAbsenceApiClient {
  private readonly baseUrl = `${GATEWAY_API_URL}/order/rider-shift`;

  async day(riderId: number, date: string): Promise<RiderShiftAbsenceDay> {
    const response = await axiosInstance.get(`${this.baseUrl}/absence`, {
      params: { riderId, date },
    });
    return unwrapRiderShiftAbsence<RiderShiftAbsenceDay>(
      response.data,
      isDay,
      'Could not load the absence marks for this day',
    );
  }

  async mark(shiftId: number, request: RiderShiftAbsenceMarkRequest): Promise<RiderShiftAbsenceEntry> {
    const response = await axiosInstance.post(`${this.baseUrl}/${shiftId}/absence`, request);
    return unwrapRiderShiftAbsence<RiderShiftAbsenceEntry>(
      response.data,
      isEntry,
      `Could not mark shift ${shiftId} absent`,
    );
  }

  async revoke(
    shiftId: number,
    request: RiderShiftAbsenceRevokeRequest,
  ): Promise<RiderShiftAbsenceEntry> {
    const response = await axiosInstance.post(`${this.baseUrl}/${shiftId}/absence/revoke`, request);
    return unwrapRiderShiftAbsence<RiderShiftAbsenceEntry>(
      response.data,
      isEntry,
      `Could not undo the absence on shift ${shiftId}`,
    );
  }
}

export default RiderShiftAbsenceApiClient;
